import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouteObject } from 'react-router-dom';

import Backdrop from '../ui/Backdrop';
import Spinner from '../ui/Spinner';

import Routes from './routes.enum';

import ScrollToTop from '../ui/tools/ScrollToTop.tsx';
import AccessTokenGuard from './components/AccessTokenGuard.tsx';
import AdminGuard from './components/AdminGuard.tsx';
import ManagerRegistrationGuard from './components/ManagerRegistrationGuard.tsx';
import RecruiterRegistrationGuard from './components/RecruiterRegistrationGuard.tsx';
import RoleGuard from './components/RoleGuard.tsx';
import CompanyVerificationGuard from './components/CompanyVerificationGuard.tsx';
import SignInRedirectGuard from './components/SignInRedirectGuard.tsx';
import BlockedAgencyGuard from './components/BlockedAgencyGuard.tsx';
import { UserRoles } from '../constants/entities.ts';
import withMe, { MePropsType } from '../hocs/withMe.tsx';
import { Children } from '../constants/types/prop.types.ts';

const SignUp = lazy(() => import('../modules/auth/sign-up'));
const SignIn = lazy(() => import('../modules/auth/sign-in'));
const ForgotPassword = lazy(() => import('../modules/auth/forgot-password'));

// Admin components
const AdminContractors = lazy(() => import('../modules/admin/contractors'));
const AdminContractorDetails = lazy(() => import('../modules/admin/contractors/contractor-details'));
const AdminFeedback = lazy(() => import('../modules/admin/feedback'));
const AdminFeedbackDetails = lazy(() => import('../modules/admin/feedback/feedback-details'));
const AdminResourcingCompanies = lazy(() => import('../modules/admin/resourcingCompanies/index.tsx'));
const AdminResourcingCompanyDetails = lazy(() => import('../modules/admin/resourcingCompanies/resourciong-company-details'));
const AdminAnalytics = lazy(() => import('../modules/admin/analytics'));
const AdminSubscription = lazy(() => import('../modules/admin/subscriptions'));

const ManagerEmailConfirmation = lazy(() => import('../modules/manager/email-confirmation'));
const ManagerProfilesSetup = lazy(() => import('../modules/manager/profiles-setup'));
const ManagerInviteMembers = lazy(() => import('../modules/manager/invite-members'));

const RecruiterSignUp = lazy(() => import('../modules/recruiter/sign-up'));
const RecruiterProfileSetup = lazy(() => import('../modules/recruiter/profile-setup'));

const Dashboard = lazy(() => import('../modules/dashboard'));
const Contracts = lazy(() => import('../modules/dashboard-modules/contracts'));
const Analytics = lazy(() => import('../modules/dashboard-modules/analytics'));
const AgencySettings = lazy(() => import('../modules/dashboard-modules/agency-settings'));
const AgencyMembers = lazy(() => import('../modules/dashboard-modules/agency-members'));
const AgencyInvites = lazy(() => import('../modules/dashboard-modules/agency-invites'));
const Inbox = lazy(() => import('../modules/dashboard-modules/inbox'));

const PersonalSettings = lazy(() => import('../modules/personal-settings'));
const PersonalProfile = lazy(() => import('../modules/personal-settings-modules/personal-profile'));
const PersonalNotifications = lazy(() => import('../modules/personal-settings-modules/personal-notifications'));

const AgencyDelete = lazy(() => import('../modules/agency-delete'));
const AgencyBlocked = lazy(() => import('../modules/common/agency-blocked'));
const ContractPage = lazy(() => import('../modules/dashboard-modules/contract-page'));
const ContractCreate = lazy(() => import('../modules/dashboard-modules/contract-create'));
const ContractEdit = lazy(() => import('../modules/dashboard-modules/contract-edit'));
const Applications = lazy(() => import('../modules/dashboard-modules/applications'));
const ApplicationDetails = lazy(() => import('../modules/dashboard-modules/applications/components/ApplicationDetails'));
const Courses = lazy(() => import('../modules/dashboard-modules/courses'));
const CourseCreate = lazy(() => import('../modules/dashboard-modules/course-create'));
const CourseDetails = lazy(() => import('../modules/dashboard-modules/course-page'));
const CourseEdit = lazy(() => import('../modules/dashboard-modules/course-edit'));

// Guard to prevent admin users from accessing regular dashboard
const NonAdminGuard = withMe(({ user, children }: MePropsType & Children) => {
  if (user?.role === UserRoles.Admin) {
    return <Navigate to={Routes.AdminDashboard} replace />;
  }
  return children;
});

function getAdminRoutes() {
  return [
    {
      path: Routes.AdminDashboard,
      element: (
        <AdminGuard>
          <Dashboard isAdmin />
        </AdminGuard>
      ),
      children: [
        {
          path: Routes.AdminAnalytics,
          element: (
            <AdminGuard>
              <AdminAnalytics />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminResourcingCompanies,
          element: (
            <AdminGuard>
              <AdminResourcingCompanies />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminResourcingCompanyDetails,
          element: (
            <AdminGuard>
              <AdminResourcingCompanyDetails />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminContractors,
          element: (
            <AdminGuard>
              <AdminContractors />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminContractorDetails,
          element: (
            <AdminGuard>
              <AdminContractorDetails />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminFeedback,
          element: (
            <AdminGuard>
              <AdminFeedback />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminFeedbackDetails,
          element: (
            <AdminGuard>
              <AdminFeedbackDetails />
            </AdminGuard>
          ),
        },
        {
          path: Routes.AdminSubscription,
          element: (
            <AdminGuard>
              <AdminSubscription />
            </AdminGuard>
          ),
        },
        {
          path: '*',
          index: true,
          element: <Navigate to={Routes.AdminAnalytics} replace />,
        },
      ],
    },
  ];
}

function getPrivateRoutes() {
  const regularRoutes: RouteObject[] = [
    { path: Routes.ManagerEmailConfirmation, element: <ManagerEmailConfirmation /> },
    { path: Routes.ManagerSetUp, element: <ManagerProfilesSetup /> },
    { path: Routes.ManagerInviteMembers, element: <ManagerInviteMembers /> },

    { path: Routes.RecruiterSetUp, element: <RecruiterProfileSetup /> },

    {
      path: Routes.Dashboard,
      element: (
        <NonAdminGuard>
          <Dashboard />
        </NonAdminGuard>
      ),
      children: [
        { path: Routes.Applications, element: <Applications /> },
        { path: Routes.Application, element: <ApplicationDetails /> },
        { path: Routes.Analytics, element: <Analytics /> },
        { path: Routes.Inbox, element: <Inbox /> },
        { path: Routes.Contracts, element: <Contracts /> },
        { path: Routes.Contract, element: <ContractPage /> },
        {
          path: Routes.ContractCreate,
          element: (
            <CompanyVerificationGuard>
              <ContractCreate />
            </CompanyVerificationGuard>
          ),
        },
        { path: Routes.ContractEdit, element: <ContractEdit /> },
        { path: Routes.Courses, element: <Courses /> },
        { path: Routes.CourseCreate, element: <CourseCreate /> },
        { path: Routes.CourseDetails, element: <CourseDetails /> },
        { path: Routes.CourseEdit, element: <CourseEdit /> },
        { path: Routes.AgencyMembers, element: <AgencyMembers /> },
        {
          path: Routes.AgencySettings,
          element: (
            <RoleGuard roles={UserRoles.AgencyManager}>
              <AgencySettings />
            </RoleGuard>
          ),
        },
        {
          path: Routes.AgencyInvites,
          element: (
            <RoleGuard roles={UserRoles.AgencyManager}>
              <AgencyInvites />
            </RoleGuard>
          ),
        },
        { path: '*', index: true, element: <Navigate to={Routes.Analytics} replace /> },
      ],
    },

    {
      path: Routes.Profile,
      element: (
        <NonAdminGuard>
          <PersonalSettings />
        </NonAdminGuard>
      ),
      children: [
        { path: Routes.PersonalSettings, element: <PersonalProfile /> },
        { path: Routes.NotificationsSettings, element: <PersonalNotifications /> },
        { path: '*', index: true, element: <PersonalProfile /> },
      ],
    },

    { path: Routes.AgencyDelete, element: <AgencyDelete /> },
    { path: Routes.AgencyBlocked, element: <AgencyBlocked /> },
  ];

  // Wrap regular routes with registration guards and blocked agency guard
  const protectedRegularRoutes = regularRoutes.map(({ element, ...route }) => ({
    ...route,
    element: (
      <BlockedAgencyGuard>
        <ManagerRegistrationGuard>
          <RecruiterRegistrationGuard>{element}</RecruiterRegistrationGuard>
        </ManagerRegistrationGuard>
      </BlockedAgencyGuard>
    ),
  }));

  // Admin routes are separate and don't need registration guards
  const adminRoutes = getAdminRoutes();

  return [...protectedRegularRoutes, ...adminRoutes];
}

function getPublicRoutes() {
  return [
    { path: Routes.SignUp, element: <SignUp /> },
    {
      path: Routes.SignIn,
      element: (
        <SignInRedirectGuard>
          <SignIn />
        </SignInRedirectGuard>
      ),
    },
    { path: Routes.ForgotPassword, element: <ForgotPassword /> },

    { path: Routes.RecruiterSignUp, element: <RecruiterSignUp /> },

    // Admin sign-in is public
    {
      path: Routes.AdminSignIn,
      element: (
        <SignInRedirectGuard>
          <SignIn isAdmin />
        </SignInRedirectGuard>
      ),
    },
  ];
}

// Custom redirect component that checks user role
const SmartRedirect = withMe(({ user }: MePropsType) => {
  // Check if user has Admin role
  if (user?.role === UserRoles.Admin) {
    return <Navigate to={Routes.AdminDashboard} replace />;
  }
  return <Navigate to={Routes.Dashboard} replace />;
});

const useRouter = () => {
  // Create a stable router configuration that includes both public and private routes
  // This prevents the router from rebuilding when access token changes
  const allRoutes = [
    ...getPublicRoutes(),
    ...getPrivateRoutes(),
  ];

  return createBrowserRouter([
    {
      path: Routes.App,
      element: (
        <Suspense
          fallback={
            <Backdrop>
              <Spinner />
            </Backdrop>
          }
        >
          <ScrollToTop>
            <Outlet />
          </ScrollToTop>
        </Suspense>
      ),

      children: [
        ...allRoutes,

        {
          path: '*',
          index: true,
          element: (
            <AccessTokenGuard>
              <SmartRedirect />
            </AccessTokenGuard>
          ),
        },
      ],
    },

    {
      index: true,
      element: (
        <AccessTokenGuard>
          <SmartRedirect />
        </AccessTokenGuard>
      ),
    },
  ]);
};

export default useRouter;
